import { useCallback } from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DropdownItemProps } from '../dropdown-items/DropdownItems';
import Dropdown from '../dropdown/Dropdown';
import Field, { FieldProps } from '../field/Field';

export interface CasFormDropdownProps<TItem, TFormValues extends FieldValues>
  extends UseControllerProps<TFormValues>,
    Partial<Pick<FieldProps, 'helperText' | 'isRequired' | 'label' | 'tooltipLabel'>> {
  items: DropdownItemProps<TItem>[];
}

export default function CasFormDropdown<TItem, TFormValues extends FieldValues>(
  props: CasFormDropdownProps<TItem, TFormValues>,
) {
  const { t } = useTranslation();

  const { disabled, label, helperText, tooltipLabel, isRequired, items, ...controllerProps } =
    props;
  const { field, fieldState, formState } = useController({ ...controllerProps });

  const onValueChanged = useCallback(
    (detail: TItem) => {
      field.onChange(detail);
    },
    [field],
  );

  const isError = !(disabled || field.disabled) && fieldState.error !== undefined;
  const errorMessage = isError ? fieldState.error?.message : undefined;
  const isDisabled = disabled || field.disabled || formState.isSubmitting;

  return (
    <Field
      key={field.name}
      uid={field.name}
      label={label ?? ''}
      tooltipLabel={tooltipLabel}
      errorMessage={errorMessage}
      helperText={helperText}
      isDisabled={isDisabled}
      isRequired={isRequired}
    >
      <Dropdown<TItem>
        uid={field.name}
        value={field.value}
        placeholder={t('SelectAnOption')}
        disabled={disabled}
        error={isError}
        onBlur={field.onBlur}
        items={props.items}
        onChange={onValueChanged}
      />
    </Field>
  );
}
